import api from "../api";
import { readinessTag } from "./integrationsBeta";
import { fetchAllPages } from "./paginatedList";

export interface ConnectionItem {
  id: number;
  client: number;
  client_name: string;
  provider: number;
  provider_name: string;
  // Optional label to tell sibling connections of one provider apart;
  // also becomes part of the AWS secret path for new credentials.
  name: string;
  type: string;
  config: Record<string, unknown>;
}

// Display label used everywhere a connection is shown: the operator-given
// name when present, otherwise the generic "Provider (TYPE)".
export const connectionLabel = (conn: ConnectionItem): string =>
  conn.name || `${conn.provider_name} (${conn.type})`;

export interface ConnectionConfigSchema {
  provider: string;
  type: string;
  config_schema: Record<string, unknown> | null;
}

// One fixed credential slot of a connection, as declared by its provider
// adapter (drives the slot-based credential UI - no free-form types).
export interface ConnectionCredentialSlot {
  type: string;
  label: string;
  required: boolean;
  required_group: number | null;
  exists: boolean;
  credential_id: number | null;
  credential_label: string;
  secret_schema: Record<string, unknown> | null;
}

export interface ConnectionCredentialTypes {
  connection: number;
  provider: string;
  connection_type: string;
  adapter_registered: boolean;
  required_groups: string[][];
  satisfied: boolean;
  credential_types: ConnectionCredentialSlot[];
}

// Pre-creation variant: slots for a (provider, type) pair before any
// connection row exists (drives the inline "New connection" form).
export type ConnectionCredentialSlots = Omit<
  ConnectionCredentialTypes,
  "connection"
>;

export interface GetConnectionsParams {
  client?: number;
  provider_slug?: string;
  type?: string;
}

const connectionTag = (id: number | "LIST") => ({
  type: "IntegrationConnection" as const,
  id,
});
// The slots response embeds "exists" flags, so it must refresh whenever a
// secret is created/updated/deleted - those mutations invalidate
// IntegrationCredential(connectionId).
const credTag = (connectionId: number) => ({
  type: "IntegrationCredential" as const,
  id: connectionId,
});

// Endpoint names are GLOBAL across the one shared createApi: with
// overrideExisting=false RTK silently drops a same-named endpoint injected by
// another slice, and every same-named hook then calls whichever definition
// loaded first. Keep these names unique repo-wide (see updateAdminConnection /
// deleteAdminConnection in adminUsers.ts - the 2026-08-22 prod incident).
export const connectionsBetaApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getConnection: builder.query<ConnectionItem, number>({
      query: (id) => ({
        url: `/integration-platform/connections/${id}/`,
        method: "GET",
      }),
      providesTags: (_result, _err, id) => [connectionTag(id)],
    }),

    getConnections: builder.query<ConnectionItem[], GetConnectionsParams>({
      queryFn: async (params, _api, _extra, fetchWithBQ) => {
        const result = await fetchAllPages<ConnectionItem>(
          fetchWithBQ,
          "/integration-platform/connections/",
          params,
        );
        if (result.error) return result;
        // Client-side safety net on top of the backend query filters.
        return {
          data: result.data.filter(
            (row) =>
              (params.client === undefined || row.client === params.client) &&
              (params.type === undefined || row.type === params.type),
          ),
        };
      },
      providesTags: [connectionTag("LIST")],
    }),

    getConnectionConfigSchema: builder.query<ConnectionConfigSchema, number>({
      query: (id) => ({
        url: `/integration-platform/connections/${id}/config-schema/`,
        method: "GET",
      }),
      keepUnusedDataFor: 60 * 60 * 24,
    }),

    getConnectionCredentialTypes: builder.query<
      ConnectionCredentialTypes,
      number
    >({
      query: (id) => ({
        url: `/integration-platform/connections/${id}/credential-types/`,
        method: "GET",
      }),
      providesTags: (_result, _err, id) => [credTag(id)],
    }),

    getConnectionCredentialTypesByType: builder.query<
      ConnectionCredentialSlots,
      { provider: string; type: string }
    >({
      query: ({ provider, type }) => ({
        url: "/integration-platform/connections/credential-types/",
        method: "GET",
        params: { provider, type },
      }),
      // Adapter-declared, static per (provider, type).
      keepUnusedDataFor: 60 * 60 * 24,
    }),

    updateConnection: builder.mutation<
      ConnectionItem,
      { id: number; config?: Record<string, unknown>; name?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/integration-platform/connections/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _err, { id }) => [
        connectionTag(id),
        connectionTag("LIST"),
        readinessTag("ALL"),
      ],
    }),

    deleteConnection: builder.mutation<void, number>({
      query: (id) => ({
        url: `/integration-platform/connections/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _err, id) => [
        connectionTag(id),
        connectionTag("LIST"),
        readinessTag("ALL"),
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetConnectionQuery,
  useGetConnectionsQuery,
  useGetConnectionConfigSchemaQuery,
  useGetConnectionCredentialTypesQuery,
  useGetConnectionCredentialTypesByTypeQuery,
  useUpdateConnectionMutation,
  useDeleteConnectionMutation,
} = connectionsBetaApi;
